import {
  ActionIcon,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
  UnstyledButton,
  MultiSelect,
} from "@mantine/core";

import { useDisclosure } from "@mantine/hooks";
import React, { useState, useRef } from "react";
import fetchApi from "../Lib/api";
import { useTranslation } from "react-i18next";
import { IconSpeakerphone } from "@tabler/icons-react";
import {
  TimePickerComponent,
  DatePickerComponent,
} from "@syncfusion/ej2-react-calendars";
import useStartupStore from "../../stores/startupStore";
import Notification from "../Lib/Notification";
import Mixpanel from "../Lib/mixpanel";

export default function Meeting({ canSchedule, slug }) {
  const [opened, { open, close }] = useDisclosure(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const { t } = useTranslation();
  const [meetingDate, setMeetingDate] = useState(null);
  const [startDateTime, setStartDateTime] = useState(null);
  const [selectedValues, setSelectedValues] = useState([]);
  const endTimeRef = useRef(null);
  const [notification, setNotification] = useState(null);
  const { members } = useStartupStore((state) => ({
    members: state.members,
  }));

  const memberNames = members.map((mbr) => ({
    value: mbr.id,
    label: mbr.first_name + " " + mbr.last_name,
  }));

  const handleDateChange = (args) => {
    setMeetingDate(args.value);
    setStartDateTime(null); // Reset start time when date changes
    if (endTimeRef.current) {
      endTimeRef.current.value = null; // Reset end time
    }
  };

  const handleStartTimeChange = (args) => {
    if (args.value) {
      // Combine the meeting date with selected time
      const selectedDate = new Date(meetingDate);
      selectedDate.setHours(args.value.getHours(), args.value.getMinutes(), 0);

      setStartDateTime(selectedDate);

      if (endTimeRef.current) {
        endTimeRef.current.value = null; // Reset end time when start time changes
        endTimeRef.current.min = selectedDate; // Ensure end time is later
      }
    }
  };

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setMeetingDate(null);
    setStartDateTime(null);
    setSelectedValues([]);
    if (endTimeRef.current) {
      endTimeRef.current.value = null;
      endTimeRef.current.min = null;
    }
  };

  const handleSchedule = async () => {
    const startTime = performance.now();
    try {
      const response = await fetchApi("/meetings", "POST", {
        slug: slug,
        meeting: {
          title,
          description,
          start_time: startDateTime,
          end_time: endTimeRef.current.value,
          meeting_date: meetingDate,
        },
        attendees: selectedValues,
      });

      const responseTime = Math.round(performance.now() - startTime);

      if (response.ok) {
        const res = await response.json();
        setNotification({ type: "success", content: res.message });
        Mixpanel.content('Meeting', 'Created', {
          creation_method: canSchedule ? 'Schedule' : 'Request',
          meeting_title: title,
          attendees_count: selectedValues.length,
          response_time_ms: responseTime,
          startup_slug: slug,
      });
        console.log(res);
      } else {
        const msg = (await response.json()).error;
        setNotification({ type: "failure", content: res.error });
        Mixpanel.error('Meeting Schedule/Request Error', {
          error_message: errorMessage,
          error_type: 'API Error',
          http_status: response.status,
          component: 'Meeting Modal',
          user_action: canSchedule ? 'Schedule Meeting' : 'Request Meeting',
          response_time_ms: responseTime,
          startup_slug: slug,
      });
        console.error(msg);
      }
    } catch (error) {
      const responseTime = Math.round(performance.now() - startTime);
      console.error("Error during password change:", error);
      Mixpanel.error('Meeting Schedule/Request Error', {
        error_message: error.message,
        error_type: 'Network Error',
        component: 'Meeting Modal',
        user_action: canSchedule ? 'Schedule Meeting' : 'Request Meeting',
        response_time_ms: responseTime,
        startup_slug: slug,
      });
    } finally {
      resetForm();
    }
  };

  return (
    <>
      {opened && (
        <Modal
          opened={opened}
          onClose={close}
          size="lg"
          radius="md"
          transitionProps={{ transition: "fade", duration: 200 }}
          withCloseButton={true}
        >
          <Stack spacing={18} className="p-5">
            {notification && <Notification {...notification} />}
            <Title order={5}>{t("startups_page.meeting.header")}</Title>
            <Text size="md">{t("startups_page.meeting.desc")}</Text>
            <TextInput
              size="md"
              label={t("startups_page.meeting.title")}
              value={title}
              onChange={(e) => setTitle(e.currentTarget.value)}
            />
            <Group grow>
              <label>Meeting Date:</label>
              <DatePickerComponent
                placeholder="Select Meeting Date"
                onChange={handleDateChange}
                min={new Date()}
              />
            </Group>

            <Group grow>
              <label>Start Time:</label>
              <TimePickerComponent
                placeholder="Select Start Time"
                enabled={!!meetingDate} // Enable only after date is selected
                onChange={handleStartTimeChange}
              />
            </Group>
            <Group grow>
              <label>End Time:</label>
              <TimePickerComponent
                ref={endTimeRef}
                placeholder="Select End Time"
                min={startDateTime} // Restrict to be later than start time
                enabled={!!startDateTime} // Enable only after start time is selected
              />
            </Group>
            <div onClick={(e) => e.stopPropagation()}>
              <MultiSelect
                data={memberNames}
                label="Select Members"
                placeholder="Choose people to invite"
                value={selectedValues} // Bound to state
                onChange={(values) => {
                  setSelectedValues(values);
                  console.log(values);
                }}
                withinPortal
              />
            </div>

            <Textarea
              size="md"
              radius="md"
              label={t("startups_page.meeting.agenda")}
              placeholder={t("startups_page.meeting.agenda_p")}
              onChange={(e) => setDescription(e.currentTarget.value)}
            />

            <Button
              onClick={handleSchedule}
              variant="default"
              fullWidth
              className="w-full px-12"
              disabled={!(title && description && startDateTime)}
            >
              {canSchedule
                ? t("startups_page.meeting.buttonText_m")
                : t("startups_page.members.request_meeting")}
            </Button>
          </Stack>
        </Modal>
      )}

      <UnstyledButton onClick={open}>
        <Group>
          <ActionIcon variant="subtle">
            <IconSpeakerphone size={24} />
          </ActionIcon>
          <p style={{ fontSize: "16px" }}>
            {canSchedule
              ? t("startups_page.members.schedule")
              : t("startups_page.members.request_meeting")}
          </p>
        </Group>
      </UnstyledButton>
    </>
  );
}
